/**
 * Centralized API Error Handler Service
 * Processes all API errors and routes them to the global error toast store
 * Handles categorization, deduplication, and server-down detection
 */

import type { AxiosError } from 'axios';
import { useErrorToastStore, type ErrorCategory } from '../stores/errorToastStore';

interface ErrorResponse {
  message?: string;
  error?: string;
  msg?: string;
  err_message?: string;
  error_message?: string;
  [key: string]: unknown;
}

class ErrorHandlerService {
  private serverDownCount = 0;
  private readonly SERVER_DOWN_THRESHOLD = 3; // Consecutive failures to detect server down
  private lastServerCheckTime = 0;
  private readonly SERVER_CHECK_INTERVAL = 10000; // 10 seconds

  /**
   * Main error handler - called from Axios interceptor
   */
  public handleError(error: AxiosError): void {
    const category = this.categorizeError(error);
    const message = this.extractErrorMessage(error);
    const type = this.determineToastType(category);

    // Handle server-down detection
    if (this.isServerDownError(error)) {
      this.handleServerDownError(error, message);
      return;
    }

    // Reset server-down counter for successful categorization
    this.resetServerDownCounter();

    // Add error to global store
    useErrorToastStore.getState().addError({
      message,
      type,
      category,
      metadata: {
        // url: error.config?.url,
        // status: error.response?.status,
        // code: error.code,
      },
    });
  }

  /**
   * Handle multiple parallel errors (e.g., from Promise.all)
   */
  public handleParallelErrors(errors: AxiosError[]): void {
    const serverDownErrors = errors.filter(this.isServerDownError);

    // If majority of errors are server-down, treat as single server outage
    if (serverDownErrors.length >= errors.length / 2) {
      this.handleServerDownError(
        serverDownErrors[0],
        'Server is unreachable. Multiple requests failed. Please try again later.'
      );
      return;
    }

    // Otherwise, display each error individually
    errors.forEach((error) => this.handleError(error));
  }

  /**
   * Categorize error based on type, status code, and error code
   */
  private categorizeError(error: AxiosError): ErrorCategory {
    // Network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return 'timeout';
      }
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        return 'network';
      }
      return 'server-down';
    }

    const status = error.response.status;

    // HTTP status code categorization
    if (status >= 500 && status < 600) {
      return status === 503 ? 'server-down' : 'server-error';
    }

    if (status >= 400 && status < 500) {
      return 'client-error';
    }

    // Application-level errors (200 with error payload)
    const data = error.response.data as ErrorResponse;
    if (data && (data.error || data.err_message || data.error_message)) {
      return 'application-error';
    }

    return 'unknown';
  }

  /**
   * Extract user-friendly error message
   */
  private extractErrorMessage(error: AxiosError): string {
    // Check for timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'Request timed out. The server is taking too long to respond. Please try again.';
    }

    // Check for network error
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return 'Unable to connect to server. Please check your internet connection.';
    }

    // Extract server message
    const responseData = error.response?.data;
    if (responseData) {
      const data = responseData as ErrorResponse;

      // Priority order for extracting message
      const serverMessage =
        data.err_message ||
        data.error_message ||
        data.message ||
        data.error ||
        data.msg;

      if (serverMessage && typeof serverMessage === 'string') {
        return serverMessage;
      }

      // If data is a string itself
      if (typeof responseData === 'string') {
        return responseData;
      }
    }

    // Status-based default messages
    const status = error.response?.status;
    if (status) {
      switch (status) {
        case 400:
          return 'Invalid request. Please check your input and try again.';
        case 401:
          return 'Authentication failed. Please log in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'Requested resource not found.';
        case 409:
          return 'A conflict occurred. The resource may have been modified.';
        case 422:
          return 'Validation failed. Please check your input.';
        case 429:
          return 'Too many requests. Please wait a moment and try again.';
        case 500:
          return 'Internal server error. Please try again later or contact support.';
        case 502:
          return 'Bad gateway. The server is temporarily unavailable.';
        case 503:
          return 'Service temporarily unavailable. Please try again later.';
        case 504:
          return 'Gateway timeout. The server took too long to respond.';
        default:
          return `An error occurred (${status}). Please try again.`;
      }
    }

    return 'An unexpected error occurred. Please try again.';
  }

  /**
   * Determine toast type based on error category
   */
  private determineToastType(
    category: ErrorCategory
  ): 'error' | 'warning' | 'network' | 'server-down' {
    switch (category) {
      case 'network':
        return 'network';
      case 'server-down':
        return 'server-down';
      case 'timeout':
        return 'warning';
      case 'client-error':
        return 'warning';
      case 'server-error':
      case 'application-error':
      case 'unknown':
      default:
        return 'error';
    }
  }

  /**
   * Check if error indicates server is down
   */
  private isServerDownError(error: AxiosError): boolean {
    // Network errors
    if (!error.response) {
      return (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ENOTFOUND' ||
        error.code === 'ETIMEDOUT'
      );
    }

    // 503 Service Unavailable
    if (error.response.status === 503) {
      return true;
    }

    // 502 Bad Gateway (often indicates server down)
    if (error.response.status === 502) {
      return true;
    }

    return false;
  }

  /**
   * Handle server-down scenario
   */
  private handleServerDownError(error: AxiosError, customMessage?: string): void {
    const now = Date.now();

    // Increment counter
    this.serverDownCount++;

    // Only show server-down toast after threshold is reached
    if (this.serverDownCount >= this.SERVER_DOWN_THRESHOLD) {
      // Check if we've recently shown a server-down message
      if (now - this.lastServerCheckTime > this.SERVER_CHECK_INTERVAL) {
        const message =
          customMessage ||
          'Server is unreachable. Please try again later or contact support.';

        useErrorToastStore.getState().setServerDownDetected(true);
        useErrorToastStore.getState().addError({
          message,
          type: 'server-down',
          category: 'server-down',
          isPersistent: true, // Server-down errors should persist
          metadata: {
            // url: error.config?.url,
            // status: error.response?.status,
            // code: error.code,
          },
        });

        this.lastServerCheckTime = now;
      }
    }
  }

  /**
   * Reset server-down counter (called on successful requests)
   */
  private resetServerDownCounter(): void {
    if (this.serverDownCount > 0) {
      this.serverDownCount = 0;
      useErrorToastStore.getState().setServerDownDetected(false);
      useErrorToastStore.getState().clearServerDownErrors();
    }
  }

  /**
   * Manual reset for server-down detection
   */
  public resetServerDown(): void {
    this.resetServerDownCounter();
  }

  /**
   * Check if a specific error should be suppressed
   * Useful for errors that components want to handle themselves
   */
  public shouldSuppressError(error: AxiosError): boolean {
    const url = error.config?.url || '';

    // Suppress auth verification errors (handled by AuthContext)
    if (url.includes('/verify') || url.includes('/select-tenant')) {
      return true;
    }

    // Suppress errors from endpoints that handle their own error display
    // (form shows user-friendly error banner instead of global toast)
    const selfHandledEndpoints = ['/submit-application', '/validate-property'];
    if (selfHandledEndpoints.some(ep => url.includes(ep))) {
      return true;
    }

    // A missing document-process config is an expected state, not a failure: the
    // uploader renders "not configured" for it. Without this, every stage that
    // mounts DocumentUploader for an unconfigured handler threw a "Process not
    // found" toast at the user.
    if (url.includes('/document-config/') && error.response?.status === 404) {
      return true;
    }

    // The Display Devices "Approve & Sign" flow probes the local dummy-signer
    // endpoint first; on a box that hasn't opted in (DIGISIGN_LOCAL_DUMMY unset)
    // it answers 404 BY DESIGN and the caller silently falls back to the normal
    // signer. That expected 404 must not raise a global "resource not found"
    // toast — it is a capability probe, not a user-facing failure.
    if (url.includes('/dev/local-sign/') && error.response?.status === 404) {
      return true;
    }

    /*
     * Display Devices dashboard panels that render their own failure state.
     *
     * Each of these is one card on a page of twenty: the trend line, the period
     * strip and the fee-only column of Amounts. When one cannot be served the
     * card says so in its own space and the rest of the dashboard is unaffected
     * — a global "contact support" toast over a working screen is both wrong
     * and unactionable.
     */
    const selfReportingPanels = ['/dashboard/trend', '/dashboard/period-pulse'];
    if (selfReportingPanels.some(ep => url.includes(ep))) {
      return true;
    }
    if (url.includes('/dashboard/stats') && url.includes('amount_basis=NET')) {
      return true;
    }

    // Suppress specific status codes that are handled elsewhere
    const suppressedStatuses = [401, 406]; // Auth errors handled by interceptor
    if (error.response && suppressedStatuses.includes(error.response.status)) {
      return true;
    }

    return false;
  }
}

// Export singleton instance
export const errorHandler = new ErrorHandlerService();

// Export for manual error handling in components if needed
export const handleApiError = (error: AxiosError) => {
  if (!errorHandler.shouldSuppressError(error)) {
    errorHandler.handleError(error);
  }
};

export const handleParallelApiErrors = (errors: AxiosError[]) => {
  const unsuppressedErrors = errors.filter(
    (error) => !errorHandler.shouldSuppressError(error)
  );
  if (unsuppressedErrors.length > 0) {
    errorHandler.handleParallelErrors(unsuppressedErrors);
  }
};
